import { memo, useState, useEffect, useCallback, type FunctionComponent, useMemo } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
// Material UI imports
import type { SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
// local imports
import CloseIconButton from './CloseIconButton';
import type { Values } from '../helpers/intl';
// SCSS imports
import { dialogActions, dialogActionsCompact, text, okButton } from './ActionSucceededMessage.module.scss';

const contentSx: SxProps<Theme> = { pb: 1 } as const;
const textPadding = { sm: 6 };

type ActionSucceededMessageProps = {
  readonly maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'; // TODO: use MUI size type
  readonly compact?: boolean;
  readonly withClose?: boolean;
  readonly disableTypography?: boolean;
  readonly onOK?: () => void;
  readonly title?: string | null;
  readonly message: string;
  readonly values?: Values;
  readonly button?: string | null;
  readonly link?: string | null;
  readonly open?: boolean;
};

const ActionSucceededMessage: FunctionComponent<ActionSucceededMessageProps> = ({
  maxWidth,
  compact,
  withClose,
  disableTypography,
  onOK,
  title,
  message,
  values,
  button,
  link,
  open = false
}) => {
  const { formatMessage } = useIntl();

  const [messageOpen, setMessageOpen] = useState(false);

  const translatedLink = useMemo(
    () => link ? formatMessage({ id: link, defaultMessage: link }) : undefined,
    [formatMessage, link]
  );

  useEffect(() => {
    if (open) setMessageOpen(true);
  }, [open]);

  const handleClose = useCallback(() => {
    setMessageOpen(false);
    if (onOK) onOK();
  }, [onOK]);

  const titleSx: SxProps<Theme> = useMemo(() => ({ pt: compact ? 5 : 8, pb: 2, px: 6, textAlign: 'center' }), [compact]);

  return (
    <Dialog
        maxWidth={maxWidth}
        open={messageOpen}
        onClose={onOK ? undefined : handleClose}
    >
      {withClose ? <CloseIconButton onClick={handleClose}/> : undefined}
      {title ? (
        <DialogTitle sx={titleSx}>
          <Typography variant="h3" component="span">
            <FormattedMessage id={title} values={values} defaultMessage={title}/>
          </Typography>
        </DialogTitle>
      ) : undefined}
      <DialogContent sx={contentSx}>
        <Box
            pt={title ? undefined : (compact && 3) || 6}
            pb={compact || onOK ? 2 : 6}
            px={textPadding}
        >
          {disableTypography ? (
            <FormattedMessage id={message} values={values} defaultMessage={message}/>
          ) : (
            <Typography variant="inherit" align="center" className={text}>
              <FormattedMessage id={message} values={values} defaultMessage={message}/>
            </Typography>
          )}
        </Box>
        {onOK && compact ? <Divider/> : undefined}
      </DialogContent>
      {onOK ? (
        <DialogActions disableSpacing className={compact ? dialogActionsCompact : dialogActions}>
          <Button
              color="primary"
              variant="contained"
              disableElevation
              size="large"
              className={okButton}
              onClick={handleClose}
              component="a"
              href={translatedLink}
              target={link ? '_blank' : undefined}
              rel={link ? 'noopener noreferrer' : undefined}
          >
            <FormattedMessage id={button || 'common.big_ok'} values={values} defaultMessage={button || undefined}/>
          </Button>
        </DialogActions>
      ) : undefined}
    </Dialog>
  );
};

export default memo(ActionSucceededMessage);
